/**
 * SSLookupConfig — Lookup Table Administration
 *
 * Lets admins manage all configurable dropdown data that drives the
 * Party Explorer forms without touching the database directly:
 *
 *   Tab 1 — Address Types        (ssaddresstypes)         e.g. Home, Work, Postal
 *   Tab 2 — Contact Channels     (sscontactmedia)         e.g. Phone, Email, WhatsApp
 *   Tab 3 — Channel Sub-types    (sscontactmedia_types)   e.g. Mobile, Landline, Work Email
 *   Tab 4 — Identifier Types     (ssidentifiertypes)      e.g. National ID, Passport, MRN
 *   Tab 5 — Party Roles          (sspartyroles)           e.g. PATIENT, NURSE, DOCTOR
 *   Tab 6 — Relationship Types   (ssparty_relationship_types) e.g. ASSIGNED_CAREGIVER
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ssLookupAPI, ssRelAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, TabBar, Section,
  Button, Badge, Field, Input, Select,
  Spinner, EmptyState, ErrorBanner, SuccessBanner,
  DataTable,
  COLOR, RADIUS, FONT,
} from '../../components/ss';

// ── helpers ────────────────────────────────────────────────────────────────────

const fmtErr = (e) => e?.response?.data?.detail || e?.message || 'Operation failed';

function InlineEditRow({ row, cols, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => {
    const f = {};
    cols.forEach(c => { f[c.key] = row?.[c.key] ?? ''; });
    return f;
  });
  return (
    <tr style={{ background: COLOR.blueBg }}>
      {cols.map(c => (
        <td key={c.key} style={{ padding: '8px 10px' }}>
          {c.type === 'select' ? (
            <Select style={{ fontSize: 13, padding: '4px 8px' }}
              value={form[c.key]} onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))}>
              <option value="">— any —</option>
              {(c.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          ) : c.type === 'bool' ? (
            <Select style={{ fontSize: 13, padding: '4px 8px' }}
              value={String(form[c.key])}
              onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value === 'true' }))}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </Select>
          ) : (
            <Input style={{ fontSize: 13, padding: '4px 8px' }}
              value={form[c.key]} onChange={e => setForm(f => ({ ...f, [c.key]: e.target.value }))} />
          )}
        </td>
      ))}
      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
        <Button size="sm" disabled={saving} onClick={() => onSave(form)}
          style={{ marginRight: 6 }}>{saving ? '…' : 'Save'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </td>
    </tr>
  );
}

// ── Generic lookup section ─────────────────────────────────────────────────────

function LookupSection({
  title, description, rows, loading, err, ok, cols,
  onAdd, onUpdate, onDelete,
  idKey, systemKey = 'internalofsystem',
}) {
  const [editId, setEditId]  = useState(null);
  const [adding, setAdding]  = useState(false);
  const [saving, setSaving]  = useState(false);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editId === '__new__') await onAdd(form);
      else                       await onUpdate(editId, form);
      setAdding(false); setEditId(null);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: COLOR.text }}>{title}</div>
          {description && <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>{description}</div>}
        </div>
        <Button size="sm" onClick={() => { setAdding(true); setEditId('__new__'); }}>+ Add</Button>
      </div>

      {loading && <Spinner />}
      {err  && <ErrorBanner style={{ marginBottom: 8 }}>{err}</ErrorBanner>}
      {ok   && <SuccessBanner style={{ marginBottom: 8 }}>{ok}</SuccessBanner>}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: COLOR.grayBg, borderBottom: `1px solid ${COLOR.grayBorder}` }}>
              {cols.map(c => (
                <th key={c.key} style={{ padding: '9px 10px', textAlign: 'left',
                  fontWeight: 700, fontSize: 11, color: COLOR.textMuted,
                  textTransform: 'uppercase', letterSpacing: '.5px' }}>
                  {c.label}
                </th>
              ))}
              <th style={{ padding: '9px 10px', width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {adding && editId === '__new__' && (
              <InlineEditRow row={null} cols={cols} saving={saving}
                onSave={handleSave} onCancel={() => { setAdding(false); setEditId(null); }} />
            )}
            {rows.length === 0 && !adding && (
              <tr><td colSpan={cols.length + 1} style={{ padding: 20, textAlign: 'center', color: COLOR.textMuted }}>
                No entries yet. Click + Add to create one.
              </td></tr>
            )}
            {rows.map(row => {
              const id = row[idKey];
              const isSystem = row[systemKey];
              return editId === id ? (
                <InlineEditRow key={id} row={row} cols={cols} saving={saving}
                  onSave={handleSave} onCancel={() => setEditId(null)} />
              ) : (
                <tr key={id} style={{ borderBottom: `1px solid ${COLOR.graySubtle}` }}
                  onMouseEnter={e => e.currentTarget.style.background = COLOR.grayBg}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  {cols.map(c => (
                    <td key={c.key} style={{ padding: '10px 10px', color: COLOR.text }}>
                      {c.render ? c.render(row[c.key], row) : (
                        c.type === 'bool'
                          ? <Badge color={row[c.key] ? 'green' : 'gray'}>{row[c.key] ? 'Yes' : 'No'}</Badge>
                          : String(row[c.key] ?? '—')
                      )}
                    </td>
                  ))}
                  <td style={{ padding: '10px 10px', whiteSpace: 'nowrap' }}>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(id)}
                      style={{ marginRight: 4 }}>Edit</Button>
                    {!isSystem && (
                      <Button size="sm" variant="danger"
                        onClick={() => { if (window.confirm('Delete this entry?')) onDelete(id); }}>✕</Button>
                    )}
                    {isSystem && (
                      <Badge color="teal" style={{ fontSize: 10 }}>system</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── Tab components ──────────────────────────────────────────────────────────────

function AddressTypesTab() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState('');
  const [ok, setOk]         = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await ssLookupAPI.listAddressTypes();
      setRows(r?.data || []);
    } catch (e) { setErr(fmtErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const wrap = async (fn, msg) => {
    setErr(''); setOk('');
    try { await fn(); setOk(msg); await load(); }
    catch (e) { setErr(fmtErr(e)); }
  };

  const COLS = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Display Name' },
    { key: 'description', label: 'Description' },
  ];

  return (
    <LookupSection
      title="Address Types" idKey="id"
      description="Controls the Address Type dropdown when adding addresses to a party (e.g. Home, Work, Postal, Billing)."
      rows={rows} loading={loading} err={err} ok={ok} cols={COLS}
      onAdd={d => wrap(() => ssLookupAPI.createAddressType(d), 'Address type created')}
      onUpdate={(id, d) => wrap(() => ssLookupAPI.updateAddressType(id, d), 'Address type updated')}
      onDelete={id => wrap(() => ssLookupAPI.deleteAddressType(id), 'Deleted')}
    />
  );
}

function ContactChannelsTab() {
  const [channels, setChannels]       = useState([]);
  const [subtypes, setSubtypes]       = useState([]);
  const [loadingC, setLoadingC]       = useState(false);
  const [loadingS, setLoadingS]       = useState(false);
  const [errC, setErrC]               = useState('');
  const [errS, setErrS]               = useState('');
  const [okC, setOkC]                 = useState('');
  const [okS, setOkS]                 = useState('');

  const loadChannels = useCallback(async () => {
    setLoadingC(true);
    try { const r = await ssLookupAPI.listContactMedia(); setChannels(r?.data || []); }
    catch (e) { setErrC(fmtErr(e)); } finally { setLoadingC(false); }
  }, []);

  const loadSubtypes = useCallback(async () => {
    setLoadingS(true);
    try { const r = await ssLookupAPI.listContactMediaTypes(); setSubtypes(r?.data || []); }
    catch (e) { setErrS(fmtErr(e)); } finally { setLoadingS(false); }
  }, []);

  useEffect(() => { loadChannels(); loadSubtypes(); }, [loadChannels, loadSubtypes]);

  const wrapC = async (fn, msg) => {
    setErrC(''); setOkC('');
    try { await fn(); setOkC(msg); await loadChannels(); }
    catch (e) { setErrC(fmtErr(e)); }
  };

  const wrapS = async (fn, msg) => {
    setErrS(''); setOkS('');
    try { await fn(); setOkS(msg); await loadSubtypes(); }
    catch (e) { setErrS(fmtErr(e)); }
  };

  const channelOptions = channels.map(c => ({ value: c.contactmediaid || c.id, label: c.name }));

  const CHAN_COLS = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Channel Name' },
    { key: 'description', label: 'Description' },
  ];

  const SUB_COLS = [
    { key: 'contactmediaid', label: 'Channel', type: 'select', options: channelOptions,
      render: (v) => {
        const c = channels.find(x => (x.contactmediaid || x.id) === v);
        return c ? <Badge color="teal">{c.name}</Badge> : String(v ?? '—');
      }
    },
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Sub-type Name' },
  ];

  return (
    <>
      <LookupSection
        title="Contact Channels" idKey="id"
        description="Top-level contact channels (e.g. Phone, Email, WhatsApp). Each can have sub-types below."
        rows={channels} loading={loadingC} err={errC} ok={okC} cols={CHAN_COLS}
        onAdd={d => wrapC(() => ssLookupAPI.createContactMedia(d), 'Channel created')}
        onUpdate={(id, d) => wrapC(() => ssLookupAPI.updateContactMedia(id, d), 'Channel updated')}
        onDelete={id => wrapC(() => ssLookupAPI.deleteContactMedia(id), 'Deleted')}
      />
      <LookupSection
        title="Channel Sub-types" idKey="id"
        description="Optional sub-types within a channel (e.g. Mobile / Landline under Phone; Work Email / Personal Email under Email)."
        rows={subtypes} loading={loadingS} err={errS} ok={okS} cols={SUB_COLS}
        onAdd={d => wrapS(() => ssLookupAPI.createContactMediaType({ ...d, contactmediaid: Number(d.contactmediaid) }), 'Sub-type created')}
        onUpdate={(id, d) => wrapS(() => ssLookupAPI.updateContactMediaType(id, d), 'Sub-type updated')}
        onDelete={id => wrapS(() => ssLookupAPI.deleteContactMediaType(id), 'Deleted')}
      />
    </>
  );
}

function IdentifierTypesTab() {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState('');
  const [ok, setOk]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await ssLookupAPI.listIdentifierTypes(); setRows(r?.data || []); }
    catch (e) { setErr(fmtErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const wrap = async (fn, msg) => {
    setErr(''); setOk('');
    try { await fn(); setOk(msg); await load(); }
    catch (e) { setErr(fmtErr(e)); }
  };

  const PARTY_TYPE_OPTS = [
    { value: '', label: 'Both (PER + ORG)' },
    { value: 'PER', label: 'Person only' },
    { value: 'ORG', label: 'Organisation only' },
  ];

  const COLS = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Display Name' },
    { key: 'allowedpartytype', label: 'Allowed For',
      type: 'select', options: PARTY_TYPE_OPTS,
      render: v => v ? <Badge color="blue">{v}</Badge> : <Badge color="gray">Both</Badge> },
    { key: 'requireissuanceauthority', label: 'Req. Authority', type: 'bool',
      render: v => <Badge color={v ? 'amber' : 'gray'}>{v ? 'Yes' : 'No'}</Badge> },
    { key: 'requireexpirationdate', label: 'Req. Expiry', type: 'bool',
      render: v => <Badge color={v ? 'amber' : 'gray'}>{v ? 'Yes' : 'No'}</Badge> },
  ];

  return (
    <LookupSection
      title="Identifier Types" idKey="id"
      description="Document / ID types shown in the Identifiers dropdown (e.g. National ID, Passport, MRN). Toggle required fields to enforce data quality."
      rows={rows} loading={loading} err={err} ok={ok} cols={COLS}
      onAdd={d => wrap(() => ssLookupAPI.createIdentifierType({
        ...d,
        requireissuanceauthority: d.requireissuanceauthority === true || d.requireissuanceauthority === 'true',
        requireexpirationdate:    d.requireexpirationdate    === true || d.requireexpirationdate    === 'true',
      }), 'Identifier type created')}
      onUpdate={(id, d) => wrap(() => ssLookupAPI.updateIdentifierType(id, {
        ...d,
        requireissuanceauthority: d.requireissuanceauthority === true || d.requireissuanceauthority === 'true',
        requireexpirationdate:    d.requireexpirationdate    === true || d.requireexpirationdate    === 'true',
      }), 'Identifier type updated')}
      onDelete={id => wrap(() => ssLookupAPI.deleteIdentifierType(id), 'Deleted')}
    />
  );
}

function PartyRolesTab() {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState('');
  const [ok, setOk]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await ssLookupAPI.listPartyRoles(); setRows(r?.data || []); }
    catch (e) { setErr(fmtErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const wrap = async (fn, msg) => {
    setErr(''); setOk('');
    try { await fn(); setOk(msg); await load(); }
    catch (e) { setErr(fmtErr(e)); }
  };

  const PARTY_TYPE_OPTS = [
    { value: '', label: 'Both' },
    { value: 'PER', label: 'Person only' },
    { value: 'ORG', label: 'Organisation only' },
  ];

  const COLS = [
    { key: 'code', label: 'Role Code' },
    { key: 'name', label: 'Display Name' },
    { key: 'allowedpartytype', label: 'Applies To',
      type: 'select', options: PARTY_TYPE_OPTS,
      render: v => v ? <Badge color="purple">{v}</Badge> : <Badge color="gray">Both</Badge> },
    { key: 'externalid', label: 'External ID' },
  ];

  return (
    <LookupSection
      title="Party Roles" idKey="partyroleid"
      description="Business roles that can be assigned to parties (e.g. PATIENT, NURSE, DOCTOR, INSURANCE). These drive the Roles tab and the role filter on the Party Explorer."
      rows={rows} loading={loading} err={err} ok={ok} cols={COLS}
      onAdd={d => wrap(() => ssLookupAPI.createPartyRole(d), 'Role created')}
      onUpdate={(id, d) => wrap(() => ssLookupAPI.updatePartyRole(id, d), 'Role updated')}
      onDelete={id => wrap(() => ssLookupAPI.deletePartyRole(id), 'Deleted')}
    />
  );
}

function RelationshipTypesTab() {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState('');
  const [ok, setOk]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await ssLookupAPI.listRelTypes(); setRows(r?.data || []); }
    catch (e) { setErr(fmtErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const wrap = async (fn, msg) => {
    setErr(''); setOk('');
    try { await fn(); setOk(msg); await load(); }
    catch (e) { setErr(fmtErr(e)); }
  };

  const COLS = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Display Name' },
    { key: 'description', label: 'Description' },
    { key: 'externalid', label: 'External ID' },
  ];

  return (
    <LookupSection
      title="Relationship Types" idKey="id"
      description="Types used when linking two parties together (e.g. ASSIGNED_CAREGIVER, DOCTOR_OF, EMPLOYED_BY). These appear in the relationship type dropdown."
      rows={rows} loading={loading} err={err} ok={ok} cols={COLS}
      onAdd={d => wrap(() => ssLookupAPI.createRelType(d), 'Relationship type created')}
      onUpdate={(id, d) => wrap(() => ssLookupAPI.updateRelType(id, d), 'Relationship type updated')}
      onDelete={id => wrap(() => ssLookupAPI.deleteRelType(id), 'Deleted')}
    />
  );
}

// ── Main page ───────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'addr',    label: '📍 Address Types' },
  { id: 'contact', label: '📞 Contact Channels' },
  { id: 'ident',   label: '🪪 Identifier Types' },
  { id: 'roles',   label: '🏷️ Party Roles' },
  { id: 'rels',    label: '🔗 Relationship Types' },
];

export default function SSLookupConfig() {
  const [tab, setTab] = useState('addr');

  return (
    <div style={{ fontFamily: FONT.family, padding: '0 0 40px' }}>
      <PageHeader
        title="Lookup Configuration"
        subtitle="Manage the configurable dropdown options used across the Parties module"
      />

      <TabBar
        tabs={TABS}
        active={tab}
        onChange={setTab}
        style={{ marginBottom: 24 }}
      />

      <div style={{ maxWidth: 900 }}>
        {tab === 'addr'    && <AddressTypesTab />}
        {tab === 'contact' && <ContactChannelsTab />}
        {tab === 'ident'   && <IdentifierTypesTab />}
        {tab === 'roles'   && <PartyRolesTab />}
        {tab === 'rels'    && <RelationshipTypesTab />}
      </div>
    </div>
  );
}
